import { useState, useEffect } from "react";
import api, { useList, errMsg, fmtRM, dayName, DAYS, apiFileUrl } from "@/lib/api";
import { PageHeader, StatCard, StatusBadge, Spinner, EmptyState, Money } from "@/components/common";
import { ParentActionCenter } from "@/pages/reschedule/ReplacementWidgets";
import UpcomingReplacementsCard from "@/components/dashboard/UpcomingReplacementsCard";
import { FormDialog } from "@/components/ResourcePage";
import { InvoiceDetailDialog } from "@/pages/admin/BillingPages";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Baby, Receipt, Bell, CalendarDays, RefreshCcw, MessageSquarePlus, FileDown, CreditCard, LogOut, CheckSquare } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { useFeatureFlags } from "@/context/FeatureFlagContext";

function ParentDropoutDialog({ open, onOpenChange, child, onDone }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!reason.trim()) { toast.error("Please tell us the reason"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/parent/students/${child.id}/dropout-request`, {
        reason, notice_date: new Date().toISOString().slice(0, 10),
      });
      toast.success(`Drop-out request submitted — effective ${r.data.effective_date}`);
      setReason(""); onDone(); onOpenChange(false);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="parent-dropout-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><LogOut className="h-5 w-5 text-amber-600" />Submit drop-out for {child?.full_name}</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="rounded-md border bg-amber-50 p-3 text-xs leading-relaxed">
            <b>30-day notice rule:</b> Effective date will be the later of <i>today + 30 days</i> or <i>end of the next full month</i>.<br />
            Your <b>non-refundable deposit</b> will automatically offset the final month&apos;s fees.<br />
            The school will confirm the effective date on/after which lessons stop.
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Reason</label>
            <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="parent-dropout-reason"
              placeholder="e.g. family relocating, schedule conflict, child taking a break, etc." />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-amber-600 hover:bg-amber-700" data-testid="parent-dropout-submit">
            {busy ? "Submitting…" : "Submit 30-day drop-out request"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ParentDashboard() {
  const { data, loading } = useList("/parent/overview");
  if (loading || !data) return <Spinner full />;
  return (
    <div data-testid="parent-dashboard">
      <PageHeader title={`Welcome, ${data.parent.full_name.split(" ")[0]}`}
        description="Your children's lessons, progress and billing at a glance." />
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4 stagger-in">
        <StatCard icon={Baby} label="Children" value={data.children.length} accent testId="stat-children" />
        <StatCard icon={CalendarDays} label="Weekly classes" value={data.schedules.length} testId="stat-classes" />
        <StatCard icon={Receipt} label="Outstanding" value={fmtRM(data.outstanding_total)}
          sub={`${data.unpaid_invoices.length} unpaid invoice(s)`} testId="stat-outstanding" />
        <StatCard icon={Bell} label="Unread notifications" value={data.unread_notifications} testId="stat-notifications" />
      </div>

      <ParentActionCenter />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-10">
        <UpcomingReplacementsCard role="parent" />
        <div className="rounded-lg border bg-card p-5">
          <h3 className="text-lg font-semibold mb-3">Weekly lessons</h3>
          {data.schedules.length === 0 ? <EmptyState text="No classes scheduled." /> : data.schedules.map((s) => (
            <div key={s.id} className="py-2 border-b last:border-0 text-sm">
              <p className="font-medium">{s.student_name} — {s.course_name}</p>
              <p className="text-xs text-muted-foreground">{dayName(s.day_of_week)} {s.start_time}–{s.end_time} with {s.teacher_name} · {s.studio_name}</p>
            </div>
          ))}
        </div>
        <div className="rounded-lg border bg-card p-5">
          <h3 className="text-lg font-semibold mb-3">Unpaid invoices</h3>
          {data.unpaid_invoices.length === 0 ? <EmptyState text="All paid up. Thank you!" /> : data.unpaid_invoices.map((i) => (
            <div key={i.id} className="flex items-center justify-between py-2 border-b last:border-0 text-sm">
              <div>
                <p className="font-mono text-xs">{i.invoice_number}</p>
                <p className="text-xs text-muted-foreground">{i.student_name} · due {i.due_date}</p>
              </div>
              <div className="flex items-center gap-2">
                <Money value={i.outstanding_amount} />
                {i.is_overdue && <StatusBadge status="overdue" />}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function ParentChildren() {
  const { data: children, loading, reload } = useList("/parent/children");
  const [dropoutFor, setDropoutFor] = useState(null);
  if (loading) return <Spinner full />;
  return (
    <div data-testid="parent-children-page">
      <PageHeader title="My children" description="Enrolled children and their weekly classes." />
      {!children || children.length === 0 ? <EmptyState text="No children linked to your account. Contact the school." /> : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 stagger-in">
          {children.map((c) => (
            <div key={c.id} className="rounded-lg border bg-card p-5" data-testid={`child-card-${c.id}`}>
              <div className="flex items-center justify-between mb-1">
                <p className="font-semibold text-lg font-display">{c.full_name}</p>
                <StatusBadge status={c.status} />
              </div>
              <p className="text-xs text-muted-foreground mb-3">Level: {c.current_level || "—"} · Enrolled {c.enrollment_date}</p>
              {(c.schedules || []).map((s) => (
                <p key={s.id} className="text-sm py-1.5 border-b last:border-0">
                  {s.course_name} · {dayName(s.day_of_week)} {s.start_time}–{s.end_time} with {s.teacher_name}
                </p>
              ))}
              {c.dropout_pending && (
                <div className="mt-3 rounded-md border border-amber-500/40 bg-amber-50 p-2 text-xs" data-testid={`dropout-pending-${c.id}`}>
                  ⚠ <b>Drop-out pending</b> — effective {c.dropout_effective_date}. School will confirm.
                </div>
              )}
              {c.dropout_button_enabled && !c.dropout_pending && c.status === "active" && (
                <Button variant="outline" size="sm" className="mt-3 text-amber-700 border-amber-500/40"
                        onClick={() => setDropoutFor(c)} data-testid={`parent-dropout-btn-${c.id}`}>
                  <LogOut className="h-4 w-4 mr-1.5" />Submit drop-out (30-day notice)
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
      <ParentDropoutDialog open={!!dropoutFor} onOpenChange={(o) => !o && setDropoutFor(null)}
                            child={dropoutFor} onDone={reload} />
    </div>
  );
}

export function ParentTimetable() {
  const { data: schedules, loading } = useList("/schedules");
  if (loading) return <Spinner full />;
  const byDay = DAYS.map((d, i) => ({ day: d, classes: (schedules || []).filter((s) => s.day_of_week === i && s.status === "active") }));
  return (
    <div data-testid="parent-timetable-page">
      <PageHeader title="Timetable" description="Weekly lessons for all your children." />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 stagger-in">
        {byDay.filter((d) => d.classes.length > 0).map((d) => (
          <div key={d.day} className="rounded-lg border bg-card p-5">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-secondary mb-3">{d.day}</p>
            {d.classes.map((c) => (
              <div key={c.id} className="py-2 border-b last:border-0">
                <p className="text-sm font-medium">{c.start_time}–{c.end_time} · {c.student_name}</p>
                <p className="text-xs text-muted-foreground">{c.course_name} with {c.teacher_name} · {c.studio_name}</p>
              </div>
            ))}
          </div>
        ))}
        {byDay.every((d) => d.classes.length === 0) && <EmptyState text="No classes scheduled." />}
      </div>
    </div>
  );
}

export function ParentAttendance() {
  const { data: attendance, loading } = useList("/attendance");
  if (loading) return <Spinner full />;
  return (
    <div data-testid="parent-attendance-page">
      <PageHeader title="Attendance" description="Lesson attendance for your children." />
      {!attendance || attendance.length === 0 ? <EmptyState text="No attendance records yet." /> : (
        <div className="rounded-lg border bg-card divide-y">
          {attendance.map((a) => (
            <div key={a.id} className="px-4 py-3 flex flex-wrap items-center gap-3 text-sm">
              <span className="font-mono text-xs w-24">{a.lesson_date}</span>
              <span className="font-medium flex-1">{a.student_name} · {a.course_name}</span>
              <StatusBadge status={a.student_status} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function ParentRemarks() {
  const { data: remarks, loading, reload } = useList("/lesson-remarks");
  const [commenting, setCommenting] = useState(null);
  const [comment, setComment] = useState("");

  const submitComment = async () => {
    try {
      await api.put(`/lesson-remarks/${commenting.id}/parent-comment`, { parent_comment: comment });
      toast.success("Comment sent to the teacher");
      setCommenting(null); setComment("");
      reload();
    } catch (e) { toast.error(errMsg(e)); }
  };

  if (loading) return <Spinner full />;
  return (
    <div data-testid="parent-remarks-page">
      <PageHeader title="Lesson remarks" description="What your children learned, homework and practice videos from teachers." />
      {!remarks || remarks.length === 0 ? <EmptyState text="No lesson remarks yet." /> : (
        <div className="space-y-3 stagger-in">
          {remarks.map((r) => (
            <div key={r.id} className="rounded-lg border bg-card p-5" data-testid={`parent-remark-${r.id}`}>
              <p className="text-xs text-muted-foreground">{r.lesson_date} · {r.student_name} · {r.course_name} with {r.teacher_name}</p>
              <p className="font-medium text-sm mt-1">{r.topic_covered}</p>
              {r.homework && <p className="text-sm mt-1"><span className="text-muted-foreground">Homework:</span> {r.homework}</p>}
              {r.teacher_comment && <p className="text-sm mt-1"><span className="text-muted-foreground">Teacher:</span> {r.teacher_comment}</p>}
              {r.media_url && <a className="text-sm text-secondary underline" href={r.media_url} target="_blank" rel="noreferrer">Watch lesson media</a>}
              {r.parent_comment ? (
                <p className="text-sm mt-2 border-l-2 border-secondary pl-3">You: {r.parent_comment}</p>
              ) : (
                <Button variant="ghost" size="sm" className="mt-2 text-secondary" onClick={() => setCommenting(r)} data-testid={`reply-remark-${r.id}`}>
                  <MessageSquarePlus className="h-4 w-4 mr-1" />Reply to teacher
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
      <Dialog open={!!commenting} onOpenChange={(o) => !o && setCommenting(null)}>
        <DialogContent data-testid="parent-comment-dialog">
          <DialogHeader><DialogTitle className="font-display">Reply to {commenting?.teacher_name}</DialogTitle></DialogHeader>
          <Textarea data-testid="parent-comment-input" rows={4} value={comment} onChange={(e) => setComment(e.target.value)}
            placeholder="Your message about this lesson..." />
          <DialogFooter>
            <Button variant="outline" onClick={() => setCommenting(null)}>Cancel</Button>
            <Button onClick={submitComment} disabled={!comment} data-testid="send-comment-button">Send</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function ParentInvoices() {
  const { data: invoices, loading, reload } = useList("/invoices");
  const { disabledKeys } = useFeatureFlags();
  const stripeEnabled = !disabledKeys.includes("finance.stripe_online_payment");
  const billplzEnabled = !disabledKeys.includes("finance.billplz_online_payment");
  const [selected, setSelected] = useState(null);
  const [payingId, setPayingId] = useState(null);
  const [verifying, setVerifying] = useState(false);
  // multi-select state: { invoice_id: true|false }. Defaults to ALL unpaid checked.
  const [picked, setPicked] = useState({});
  const [bulkBusy, setBulkBusy] = useState(false);

  // Reset picks whenever invoices reload — default = ALL outstanding selected ("pay all" default).
  useEffect(() => {
    if (!invoices) return;
    setPicked(Object.fromEntries(
      invoices.filter((i) => (i.outstanding_amount || 0) > 0).map((i) => [i.id, true])));
  }, [invoices]);

  // returning from Stripe: poll the session until paid (max 5 attempts)
  useEffect(() => {
    const sessionId = new URLSearchParams(window.location.search).get("session_id");
    if (!sessionId) return;
    window.history.replaceState({}, "", window.location.pathname);
    setVerifying(true);
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/stripe/status/${sessionId}`);
        if (data.payment_status === "paid") {
          const msg = data.kind === "bulk_invoice"
            ? `Payment received — RM ${data.amount.toFixed(2)} applied across ${data.invoice_ids?.length || ""} invoices. Thank you!`
            : `Payment received — ${fmtRM(data.amount)} applied to your invoice. Thank you!`;
          toast.success(msg);
          setVerifying(false);
          reload();
          return;
        }
        if (data.status === "expired") {
          toast.error("Payment session expired — please try again.");
          setVerifying(false);
          return;
        }
      } catch { /* keep polling */ }
      if (attempts < 5) setTimeout(poll, 2000);
      else {
        toast.warning("Payment is still processing — refresh this page in a moment.");
        setVerifying(false);
      }
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const payNow = async (e, inv) => {
    e.stopPropagation();
    setPayingId(inv.id);
    try {
      const { data } = await api.post("/stripe/checkout", {
        invoice_id: inv.id, origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(errMsg(err));
      setPayingId(null);
    }
  };

  const payWithBillplz = async (e, inv) => {
    e.stopPropagation();
    setPayingId(inv.id + "-billplz");
    try {
      const { data } = await api.post("/payments/billplz/create", { invoice_id: inv.id });
      window.location.href = data.payment_url;
    } catch (err) {
      toast.error(errMsg(err));
      setPayingId(null);
    }
  };

  // Compute selected total
  const selectedIds = Object.entries(picked).filter(([, v]) => v).map(([k]) => k);
  const selectedInvoices = (invoices || []).filter((i) => selectedIds.includes(i.id) && (i.outstanding_amount || 0) > 0);
  const selectedTotal = selectedInvoices.reduce((s, i) => s + (i.outstanding_amount || 0), 0);
  const unpaidCount = (invoices || []).filter((i) => (i.outstanding_amount || 0) > 0).length;

  const paySelected = async () => {
    if (selectedInvoices.length === 0) { toast.error("Select at least one invoice"); return; }
    setBulkBusy(true);
    try {
      const { data } = await api.post("/stripe/checkout-bulk", {
        invoice_ids: selectedInvoices.map((i) => i.id),
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(errMsg(err));
      setBulkBusy(false);
    }
  };

  if (loading) return <Spinner full />;
  return (
    <div data-testid="parent-invoices-page">
      <PageHeader title="Invoices" description="Pay all your outstanding invoices in one click, or pick and choose which to pay." />
      {verifying && (
        <div className="rounded-lg border bg-accent p-4 mb-4 text-sm font-medium" data-testid="payment-verifying-banner">
          Verifying your payment with the bank…
        </div>
      )}

      {/* Sticky Pay-all summary bar (Stripe-based bulk checkout) */}
      {stripeEnabled && unpaidCount > 1 && (
        <div className="sticky top-2 z-10 mb-4 rounded-lg border-2 border-secondary/40 bg-card shadow-md p-4 flex flex-wrap items-center gap-3"
             data-testid="pay-all-bar">
          <CheckSquare className="h-5 w-5 text-secondary" />
          <div className="flex-1 min-w-[180px]">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">
              {selectedInvoices.length === unpaidCount
                ? `Pay all ${unpaidCount} outstanding invoices`
                : `${selectedInvoices.length} of ${unpaidCount} selected`}
            </p>
            <p className="text-2xl font-bold text-secondary" data-testid="pay-all-total">{fmtRM(selectedTotal)}</p>
          </div>
          <Button
            size="lg" onClick={paySelected}
            disabled={bulkBusy || selectedTotal < 0.01}
            data-testid="pay-all-button"
            className="bg-secondary hover:bg-secondary/90 text-white">
            <CreditCard className="h-4 w-4 mr-1.5" />
            {bulkBusy ? "Redirecting…" : `Pay ${fmtRM(selectedTotal)} now`}
          </Button>
        </div>
      )}

      {!invoices || invoices.length === 0 ? <EmptyState text="No invoices yet." /> : (
        <div className="space-y-3 stagger-in">
          {invoices.map((i) => {
            const isPayable = (i.outstanding_amount || 0) > 0;
            return (
              <div key={i.id}
                className={`rounded-lg border bg-card p-5 flex flex-wrap items-center gap-4 lift ${isPayable && picked[i.id] ? "border-secondary/50" : ""}`}
                data-testid={`parent-invoice-${i.id}`}>
                {isPayable && stripeEnabled && unpaidCount > 1 && (
                  <Checkbox
                    checked={!!picked[i.id]}
                    onCheckedChange={(v) => setPicked((p) => ({ ...p, [i.id]: !!v }))}
                    onClick={(e) => e.stopPropagation()}
                    data-testid={`invoice-checkbox-${i.id}`} />
                )}
                <div className="flex-1 min-w-[200px] cursor-pointer" onClick={() => setSelected(i.id)}>
                  <p className="font-mono text-xs">{i.invoice_number}</p>
                  <p className="text-sm font-medium">{i.student_name} · {i.invoice_month}</p>
                  <p className="text-xs text-muted-foreground">Due {i.due_date}</p>
                </div>
                <div className="text-right">
                  <Money value={i.total_fees_collected} className="font-bold" />
                  {i.outstanding_amount > 0 && (
                    <p className="text-xs text-[#D90429]">Outstanding {fmtRM(i.outstanding_amount)}</p>
                  )}
                </div>
                <div className="flex gap-1.5 items-center">
                  <StatusBadge status={i.status} />
                  {i.is_overdue && <StatusBadge status="overdue" />}
                  {isPayable && stripeEnabled && (
                    <Button size="sm" variant="outline" disabled={payingId === i.id}
                      onClick={(e) => payNow(e, i)} data-testid={`pay-now-${i.id}`}>
                      <CreditCard className="h-4 w-4 mr-1" />
                      {payingId === i.id ? "Redirecting…" : "Card (Stripe)"}
                    </Button>
                  )}
                  {isPayable && billplzEnabled && (
                    <Button size="sm" disabled={payingId === i.id + "-billplz"}
                      onClick={(e) => payWithBillplz(e, i)}
                      data-testid={`pay-billplz-${i.id}`}
                      className="bg-[#2D6A4F] hover:bg-[#245C42] text-white">
                      {payingId === i.id + "-billplz" ? "Redirecting…" : "Pay with Billplz (FPX)"}
                    </Button>
                  )}
                  {isPayable && !stripeEnabled && !billplzEnabled && (
                    <span className="text-xs text-muted-foreground italic">
                      Online payment temporarily unavailable — contact school
                    </span>
                  )}
                  <Button variant="ghost" size="sm"
                    onClick={(e) => { e.stopPropagation(); window.open(apiFileUrl(`/invoices/${i.id}/pdf`), "_blank"); }}
                    data-testid={`download-invoice-${i.id}`}>
                    <FileDown className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
      <InvoiceDetailDialog invoiceId={selected} open={!!selected} onOpenChange={(o) => !o && setSelected(null)} onChanged={reload} readOnly />
    </div>
  );
}

export function ParentPaymentStatus() {
  const { data, loading } = useList("/parent/overview");
  if (loading || !data) return <Spinner full />;
  return (
    <div data-testid="parent-payment-status-page">
      <PageHeader title="Payment status" description="Outstanding balances across all your children." />
      <div className="rounded-lg border bg-primary text-primary-foreground p-8 max-w-md mb-8">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary-foreground/60">Total outstanding</p>
        <p className="text-4xl font-bold font-display mt-2" data-testid="outstanding-total">{fmtRM(data.outstanding_total)}</p>
        <p className="text-xs text-primary-foreground/60 mt-2">
          {data.unpaid_invoices.length === 0 ? "All invoices settled — thank you!" : `${data.unpaid_invoices.length} invoice(s) awaiting payment`}
        </p>
      </div>
      {data.unpaid_invoices.length > 0 && (
        <div className="space-y-3">
          {data.unpaid_invoices.map((i) => (
            <div key={i.id} className="rounded-lg border bg-card p-5 flex items-center gap-4">
              <div className="flex-1">
                <p className="font-mono text-xs">{i.invoice_number}</p>
                <p className="text-sm">{i.student_name} · due {i.due_date}</p>
              </div>
              <Money value={i.outstanding_amount} className="font-bold" />
              {i.is_overdue && <StatusBadge status="overdue" />}
            </div>
          ))}
          <p className="text-xs text-muted-foreground">
            Please make payment via bank transfer to the school account and contact the office to confirm.
          </p>
        </div>
      )}
    </div>
  );
}

export function ParentNotifications() {
  const { data: notifications, loading, reload } = useList("/notifications");
  const markRead = async (id) => {
    await api.post(`/notifications/${id}/read`);
    reload();
  };
  if (loading) return <Spinner full />;
  return (
    <div data-testid="parent-notifications-page">
      <PageHeader title="Notifications" description="Updates about reschedules, approvals and school announcements." />
      {!notifications || notifications.length === 0 ? <EmptyState text="No notifications." /> : (
        <div className="space-y-2 stagger-in">
          {notifications.map((n) => (
            <div key={n.id}
              className={`rounded-lg border p-4 flex items-start gap-3 ${n.read_status ? "bg-card opacity-70" : "bg-accent border-primary/20"}`}
              data-testid={`notification-${n.id}`}>
              <Bell className="h-4 w-4 mt-0.5 text-secondary shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-semibold">{n.title}</p>
                <p className="text-sm text-muted-foreground">{n.message}</p>
                <p className="text-[11px] text-muted-foreground mt-1">{n.created_at?.slice(0, 16).replace("T", " ")}</p>
              </div>
              {!n.read_status && (
                <Button variant="ghost" size="sm" onClick={() => markRead(n.id)} data-testid={`mark-read-${n.id}`}>
                  Mark read
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
